/**
 * data.ts — read the configurator catalog that sections/configurator.liquid
 * server-renders into <script type="application/json" id="configurator-data">,
 * parse it into typed objects, derive filter facets from charm values, and
 * expose live inventory.
 *
 * The Liquid section already maps raw metafield keys to the flat field names
 * below (size/material/theme/color/waterproof, etc.), so this module reads a
 * clean shape — see docs/configurator-fixture.json for the contract. The only
 * normalization here is size labels → geometry size codes and value coercion.
 *
 * Everything downstream (facets, inventory, geometry, state) consumes the
 * resulting Catalog the same way regardless of where the data came from.
 */

import type { CharmSize } from './geometry';
import {
  BASE_CONFIG, createBuild, setChain, setClosure, addCharm,
  type BaseKind, type ChainProduct, type CharmProduct, type ChainVariant,
  type BuildState, type BuildJSON, type ClosureSelection,
} from './state';

// ─── Flat blob shapes (what the Liquid section renders) ──────────────────────

export interface RawChainVariant {
  variantId: string;
  type: BaseKind;   // 'bracelet' | 'necklace' ("For" option)
  length: number;   // cm ("Length" option, price-tier label)
  price: number;    // major currency units
  inventory: number;
}

/** A closure product (separate, own stock & price). `colors` come from the
 *  shopify.color-pattern metafield; a metal colour gates it to matching chains,
 *  a non-metal colour (e.g. "Red") makes it universal. */
export interface RawClosure {
  id: string;
  variantId: string;
  title: string;
  price: number;            // major currency units
  inventory: number;
  image: string | null;     // configurator.closure_image — the clasp PNG on the canvas
  colors: string[];         // shopify.color-pattern labels
  forBag?: boolean;         // from the bag-charm closures collection (vs ring closures)
  forKeychain?: boolean;    // from the keychain closures collection
}

export interface RawChain {
  id: string;
  title: string;
  chainType: string;
  ringImage: string | null;      // configurator.chain_image — the loop PNG used ON THE CANVAS
  closeUpImage: string | null;   // configurator.chain_close_up_image — the texture shot for the sidebar card
  closeUpFocal?: string | null;  // focal point (object-position) for the close-up card
  colors?: string[];             // shopify.color-pattern labels — used to match closures
  variants: RawChainVariant[];
  lengthRules?: {                // paopao length metafields (decimal-input + surcharge); per base
    bracelet: { min: number; max: number } | null;
    necklace: { min: number; max: number; base: number } | null;
    surchargePerCm: number;
  } | null;
}

export interface RawCharm {
  id: string;
  title: string;
  price: number;
  charmImage: string | null;
  inventory: number;        // live; 0 = sold out
  size: string;             // 'Extra Small'..'Extra Large' or 'XS'..'XL'
  material: string | null;
  theme: string[] | string | null;
  color: string[] | string | null;
  waterproof: boolean;
  original?: boolean;       // PaoPao Original → shows an "Original" badge in the library
  variantId?: string;       // not in the spec shape yet — needed for cart later
}

/** Step-1 base card — authored in the section settings (always 4, fixed). The
 *  `fromPrice` here is a fallback; the live "from" price is computed from the
 *  cheapest chain variant of that base where one exists (see baseFromPrice). */
export interface RawBase {
  kind: BaseKind;
  label: string;
  image: string | null;
  maxCharms: number;
  fromPrice: number;
  comingSoon?: boolean;          // step-1 "Coming soon" badge (section setting)
}

export interface ConfiguratorData {
  chains: RawChain[];
  charms: RawCharm[];
  charmsTotalPages?: number;   // pagination: total charm pages (50/page); page 1 is inline
  charmSort?: 'collection' | 'size_asc' | 'size_desc'; // admin `charm_sort` setting; 'collection' = keep collection order
  palettes?: RawPalette[];     // multi-colour bead palettes (bag_charm / keychain); one entry per base
  bases?: RawBase[];   // step-1 base cards (from section settings)
  step0?: Partial<Step0Content>;       // step-0 intro/preloader (title, button, media)
  step1?: { heading?: string; subtext?: string };
  lengthHelp?: Partial<Record<BaseKind, string>>; // per-base copy under the "Chain length" picker ('' = hidden)
  beadsHelp?: string;   // customisable note above the bead-palette options ('' = hidden)
  sizes?: RawSizes;     // merchant-tunable charm/closure size multipliers (section ranges)
  surchargeVariantId?: string | null; // "Length surcharge" helper variant (cart fee line)
  surchargeUnitPrice?: number;        // that helper variant's unit price (the per-unit charging granularity)
  closures?: RawClosure[]; // separate closure products (own stock + price)
  backdropImage?: string | null; // settings.backdrop_originals — the PaoPao Originals card backdrop
  backdropFocal?: string;        // focal point (object-position) for the backdrop
  reviewBackdrop?: string | null;       // step-3 backdrop image (section setting, desktop)
  reviewBackdropFocal?: string;         // focal point for the desktop review backdrop
  reviewBackdropMobile?: string | null; // step-3 backdrop image (mobile override)
  reviewBackdropMobileFocal?: string;   // focal point for the mobile review backdrop
  bundleParentVariantId?: string | null; // parent variant the cart bundles into (per store)
  originalBadgeIcon?: string | null;     // settings.product_badge_originals_icon (PaoPao Original badge)
}

/** Step-0 intro screen content (section settings). */
export interface Step0Content {
  title: string;
  buttonLabel: string;
  image: string | null;
  imageMobile: string | null;
  video: string | null;
  videoMobile: string | null;
  focalDesktop: string;   // object-position for the desktop image (focal point)
  focalMobile: string;
  icon: string;        // decorative shape key (see icons.ts DECOR_ICONS)
  iconColor: string;
}

// ─── Size normalization (label OR code → geometry code) ──────────────────────

const SIZE_LABEL_TO_CODE: Record<string, CharmSize> = {
  'Extra Small': 'XS',
  'Small':       'S',
  'Medium':      'M',
  'Large':       'L',
  'Extra Large': 'XL',
};
const SIZE_ORDER: CharmSize[] = ['XS', 'S', 'M', 'L', 'XL'];

/** Map a size value to a geometry CharmSize. Accepts a code ('L') or a label
 *  ('Large'); falls back to M. */
export function sizeCode(v: string | null | undefined): CharmSize {
  if (!v) return 'M';
  if ((SIZE_ORDER as string[]).includes(v)) return v as CharmSize; // already a code
  return SIZE_LABEL_TO_CODE[v] ?? 'M';
}

function toArray(v: string[] | string | null | undefined): string[] {
  if (v == null) return [];
  if (Array.isArray(v)) return v.map(x => String(x).trim()).filter(Boolean);
  const s = String(v).trim();
  return s ? [s] : [];
}

// ─── Catalog item shapes ─────────────────────────────────────────────────────

export interface CharmAttributes {
  size: CharmSize;            // mapped code, drives geometry spacing
  sizeLabel: string | null;   // original value, e.g. 'L' or 'Large'
  material: string | null;
  theme: string[];
  colors: string[];
  waterproof: boolean;
  original: boolean;          // PaoPao Original
}

export interface CharmCatalogItem {
  productId: string;
  variantId: string;          // '' until the Liquid section emits it (cart step)
  title: string;              // denormalized for fulfillment
  price: number;
  image: string | null;       // charm PNG
  available: number;          // live inventory; 0 = sold out
  attributes: CharmAttributes;
}

export interface ChainCatalogItem extends ChainProduct {
  image: string | null;          // ring PNG for the canvas
  closeUpImage: string | null;   // texture shot for the sidebar selector card
  closeUpFocal: string;          // object-position (focal point) for the close-up
  colors: string[];              // shopify.color-pattern labels — closure metal matching
}

/** A closure product offered in the editor (separate from the chain). */
export interface ClosureItem {
  id: string;                    // gid://shopify/Product/...
  variantId: string;             // gid://shopify/ProductVariant/...
  title: string;
  price: number;
  available: number;             // live inventory; 0 = sold out
  image: string | null;          // clasp PNG composited on the canvas
  colors: string[];              // shopify.color-pattern labels
  forBag: boolean;               // bag-charm closure (separate collection) vs ring closure
  forKeychain: boolean;          // keychain closure (separate collection)
}

export interface BaseCard {
  kind: BaseKind;
  label: string;
  image: string | null;
  maxCharms: number;
  fromPrice: number;             // fallback; see baseFromPrice()
  comingSoon: boolean;           // show a "Coming soon" badge on the step-1 card
}

/**
 * Charm/closure size multipliers (set via the section's size ranges). `charm` scales the
 * charm footprint per base (ring bases: × the ring-derived size; bag charm: × the base-image
 * width). `closure` scales the carabiner width for the bases that have a tunable one (bag
 * charm: fraction of the base width; keychain: fraction of the ring diameter). All are plain
 * multipliers; defaults mirror the previously hard-coded values.
 */
export interface SizeSettings {
  charm: { bracelet: number; necklace: number; bag_charm: number; keychain: number };
  closure: { bag_charm: number; keychain: number };
}
export interface RawSizes {
  charm?: Partial<SizeSettings['charm']>;
  closure?: Partial<SizeSettings['closure']>;
}
export const DEFAULT_SIZES: SizeSettings = {
  charm: { bracelet: 1, necklace: 0.66, bag_charm: 1.6, keychain: 1.6 },
  closure: { bag_charm: 0.13, keychain: 0.16 },
};

export interface Catalog {
  chains: ChainCatalogItem[];
  charms: CharmCatalogItem[];
  bases: BaseCard[];             // step-1 base cards (always 4)
  step0: Step0Content;           // step-0 intro/preloader screen
  step1: { heading: string; subtext: string };  // step-1 intro copy
  lengthHelp: Record<BaseKind, string>; // per-base copy under the chain-length picker ('' = hidden)
  beadsHelp: string;             // note above the bead-palette options ('' = hidden)
  backdropImage: string | null;  // PaoPao Originals card backdrop (settings.backdrop_originals)
  backdropFocal: string;         // object-position (focal point) for the backdrop
  reviewBackdrop: string | null;       // step-3 backdrop (section setting; falls back to backdropImage)
  reviewBackdropFocal: string;         // object-position (focal point) for the desktop review backdrop
  reviewBackdropMobile: string | null; // step-3 mobile backdrop override (falls back to desktop)
  reviewBackdropMobileFocal: string;   // object-position (focal point) for the mobile review backdrop
  bundleParentVariantId: string | null; // cart-bundle parent variant GID (per store; null = not configured)
  originalBadgeIcon: string | null;     // PaoPao Original badge icon (theme setting)
  closures: ClosureItem[];              // separate closure products (own stock + price)
  palettes: PaletteSet[];               // multi-colour bead palettes (bag_charm / keychain)
  sizes: SizeSettings;                  // merchant-tunable charm/closure size multipliers
  surchargeVariantId: string | null;    // helper variant for the length surcharge fee line
  surchargeUnitPrice: number;            // that helper's unit price (€ per unit; fee qty = surcharge ÷ this)
  facets: Facet[];
}

// ─── Bead palettes (bag_charm / keychain: up to 3 colours or Multicolour) ────
//
// One product per base, one VARIANT per colour combination. Rather than add a new
// pipeline, each combination is registered as a synthetic (length-less) ChainCatalogItem
// in catalog.chains — so the canvas image, pricing, cart line, serialize/hydrate all
// keep resolving it by `chain.id`, unchanged. The colour chips (ui.ts) resolve a
// selected colour-set → the matching combo → its synthetic chain via setChain.

export interface RawCombo {
  variantId: string;
  title?: string;
  price: number;
  inventory: number;
  image: string | null;   // configurator.combo_image — the combo's canvas PNG
  isMulti?: boolean;      // the exclusive Multicolour combo (colours empty)
  colors: string[];       // the 1–3 colour labels (configurator.combo_colors)
}
export interface RawPalette {
  base: string;           // 'bag_charm' | 'keychain'
  productId: string;
  title: string;
  combos: RawCombo[];
}

/** A resolved colour combination: its synthetic chain (carries image + variant/price). */
export interface Combo {
  key: string;            // canonical key (sorted lowercase colours, or MULTI_KEY)
  colors: string[];       // normalised (lowercase) colour labels; [] for multi
  isMulti: boolean;
  chain: ChainCatalogItem;
}
/** All combinations + the selectable colour set for one base. */
export interface PaletteSet {
  base: BaseKind;
  productId: string;
  title: string;
  colors: string[];                 // union of colours across combos, first-appearance order
  comboByKey: Map<string, Combo>;   // canonical key → combo
}

/** Reserved key for the exclusive Multicolour option. */
export const MULTI_KEY = '__multi__';

/** Canonical, order-independent key for a set of colours (sorted lowercase, '|'-joined). */
export function canonicalKey(colors: string[]): string {
  return colors.map(c => normColor(c)).filter(Boolean).sort().join('|');
}

const comboChainId = (productId: string, key: string): string => `${productId}::${key}`;

/**
 * Parse the raw palettes into PaletteSets AND append a synthetic length-less
 * ChainCatalogItem per combo to `chains` (mutated) so downstream resolves by id.
 */
function parsePalettes(raw: RawPalette[], chains: ChainCatalogItem[]): PaletteSet[] {
  return (raw ?? []).map(rp => {
    const base = normBase(rp.base);
    const comboByKey = new Map<string, Combo>();
    const colorOrder: string[] = [];
    const seen = new Set<string>();
    for (const rc of rp.combos ?? []) {
      const isMulti = !!rc.isMulti;
      const labels = (rc.colors ?? []).map(s => String(s).trim()).filter(Boolean);
      if (!isMulti && labels.length === 0) continue;         // unauthored variant → skip
      const key = isMulti ? MULTI_KEY : canonicalKey(labels);
      if (comboByKey.has(key)) continue;                     // dedupe
      const chain: ChainCatalogItem = {
        id: comboChainId(rp.productId, key),
        title: rp.title,
        chainType: isMulti ? 'Multicolour' : labels.join(' + '),
        variants: [{ variantId: rc.variantId, base, lengthCm: 0, price: Number(rc.price) || 0, inventory: Number(rc.inventory) || 0 }],
        image: rc.image || null,
        closeUpImage: rc.image || null,
        closeUpFocal: '50% 50%',
        colors: labels,
      };
      chains.push(chain);
      comboByKey.set(key, { key, colors: labels.map(normColor), isMulti, chain });
      if (!isMulti) for (const l of labels) {
        const nk = normColor(l);
        if (!seen.has(nk)) { seen.add(nk); colorOrder.push(l); }
      }
    }
    return { base, productId: rp.productId, title: rp.title, colors: colorOrder, comboByKey };
  });
}

/** The palette (colour chips + combos) for a base, or undefined (ring bases). */
export function paletteForBase(catalog: Catalog, base: BaseKind): PaletteSet | undefined {
  return catalog.palettes.find(p => p.base === base);
}

/** The combo currently resolved onto the build (by the selected chain id), if any. */
export function comboForChain(pal: PaletteSet, chainId: string | undefined): Combo | undefined {
  if (!chainId) return undefined;
  for (const c of pal.comboByKey.values()) if (c.chain.id === chainId) return c;
  return undefined;
}

// ─── Closures (separate products, metal-matched to the chain) ────────────────

/** Colours that read as a chain "metal": a closure carrying one of these only
 *  shows on a chain of the same metal. Any other colour (e.g. "Red") is universal. */
const METAL_COLORS = new Set(['gold', 'silver', 'rose gold', 'rosegold', 'rose-gold', 'yellow gold', 'white gold']);

const normColor = (s: string): string => s.toLowerCase().trim();
const isMetalColor = (s: string): boolean => METAL_COLORS.has(normColor(s));

/**
 * Closures available for a given chain. A closure whose colours include NO metal
 * (e.g. "Red") is universal — it shows on every chain. A closure with a metal
 * colour only shows on a chain sharing that metal (gold clasp → gold chain).
 */
export function closuresForChain(catalog: Catalog, chain: ChainProduct | null): ClosureItem[] {
  // The beads bases each draw from their own closures collection (a colour palette, not a
  // metal, so metal-matching would hide every carabiner): bag charm → forBag, keychain →
  // forKeychain. Ring bases use the metal-matched general closures (never the beads ones).
  if (chain && chain.variants.some(v => v.base === 'bag_charm')) return catalog.closures.filter(cl => cl.forBag);
  if (chain && chain.variants.some(v => v.base === 'keychain')) return catalog.closures.filter(cl => cl.forKeychain);
  const chainMetals = new Set((chain && 'colors' in chain ? (chain as ChainCatalogItem).colors : []).map(normColor).filter(isMetalColor));
  return catalog.closures.filter(cl => {
    if (cl.forBag || cl.forKeychain) return false;   // beads-base closures never show on rings
    const metals = cl.colors.map(normColor).filter(isMetalColor);
    if (metals.length === 0) return true;            // non-metal closure → universal
    return metals.some(m => chainMetals.has(m));     // metal closure → must match the chain
  });
}

/** Canvas clasp image for a selected closure (by product id), or null. */
export function closureImage(catalog: Catalog, closure: { id: string } | null): string | null {
  if (!closure) return null;
  return catalog.closures.find(c => c.id === closure.id)?.image ?? null;
}

/** Project a closure catalog item into the build's lightweight selection shape. */
export function toClosureSelection(item: ClosureItem): ClosureSelection {
  return { id: item.id, variantId: item.variantId, title: item.title, price: item.price };
}

/** Live "from" price for a base: the cheapest chain variant of that base (e.g. bracelet =
 *  cheapest 'For: Bracelet' chain; bag charm = cheapest beaded chain). Keychain has no own
 *  products yet, so it borrows the beaded-chain (bag charm) prices. Falls back to the
 *  authored section price only when no products exist at all. */
export function baseFromPrice(catalog: Catalog, base: BaseKind): number {
  const lowestFor = (b: BaseKind): number => {
    let min = Infinity;
    for (const c of catalog.chains) for (const v of c.variants) if (v.base === b) min = Math.min(min, v.price);
    return min;
  };
  let min = lowestFor(base);
  if (min === Infinity && base === 'keychain') min = lowestFor('bag_charm'); // keychain → beaded-chain prices
  if (min !== Infinity) return min;
  return catalog.bases.find(b => b.kind === base)?.fromPrice ?? 0;
}

// ─── Facets (generated from charm values, never hardcoded) ───────────────────

export interface FacetValue { value: string; count: number; }
export interface Facet { key: string; label: string; values: FacetValue[]; }

interface FacetDef {
  key: string;
  label: string;
  /** the charm's value(s) for this facet — also reused by filterCharms */
  extract: (c: CharmCatalogItem) => string[];
}

const FACET_DEFS: FacetDef[] = [
  { key: 'size',       label: 'Size',       extract: c => (c.attributes.sizeLabel ? [c.attributes.sizeLabel] : []) },
  { key: 'material',   label: 'Material',   extract: c => (c.attributes.material ? [c.attributes.material] : []) },
  { key: 'theme',      label: 'Theme',      extract: c => c.attributes.theme },
  { key: 'color',      label: 'Color',      extract: c => c.attributes.colors },
  { key: 'waterproof', label: 'Waterproof', extract: c => [c.attributes.waterproof ? 'Waterproof' : 'Not waterproof'] },
  { key: 'original',   label: 'PaoPao Original', extract: c => [c.attributes.original ? 'Original' : 'Not original'] },
];

function sizeRank(label: string): number {
  const i = SIZE_ORDER.indexOf(sizeCode(label));
  return i === -1 ? SIZE_ORDER.length : i;
}

/** Distinct facet values with counts, derived from the charm set. */
export function buildFacets(charms: CharmCatalogItem[]): Facet[] {
  const facets: Facet[] = [];
  for (const def of FACET_DEFS) {
    const counts = new Map<string, number>();
    for (const c of charms) for (const v of def.extract(c)) counts.set(v, (counts.get(v) ?? 0) + 1);
    if (counts.size === 0) continue; // nothing to filter by → no facet

    const values = [...counts.entries()].map(([value, count]) => ({ value, count }));
    if (def.key === 'size') values.sort((a, b) => sizeRank(a.value) - sizeRank(b.value));
    else values.sort((a, b) => a.value.localeCompare(b.value));

    facets.push({ key: def.key, label: def.label, values });
  }
  return facets;
}

/** key → selected values. OR within a facet, AND across facets. */
export type FacetSelection = Record<string, string[]>;

export function filterCharms(charms: CharmCatalogItem[], selection: FacetSelection): CharmCatalogItem[] {
  const active = Object.entries(selection).filter(([, vals]) => vals && vals.length);
  if (active.length === 0) return charms.slice();

  const byKey = new Map(FACET_DEFS.map(d => [d.key, d] as const));
  return charms.filter(c =>
    active.every(([key, vals]) => {
      const def = byKey.get(key);
      if (!def) return true;
      return def.extract(c).some(v => vals.includes(v));
    }),
  );
}

// ─── Live inventory (read-only) ──────────────────────────────────────────────

export function isSoldOut(c: CharmCatalogItem): boolean {
  return c.available <= 0;
}

// ─── Parsing ─────────────────────────────────────────────────────────────────

// "For" option value → BaseKind. The Liquid downcases it; we also collapse spaces/hyphens
// to underscores so "Bag charm" / "Bag Charm" → 'bag_charm' (bracelet/necklace pass through).
function normBase(v: string): BaseKind {
  return (v || '').toLowerCase().trim().replace(/[\s-]+/g, '_') as BaseKind;
}

function parseChain(raw: RawChain): ChainCatalogItem {
  const variants: ChainVariant[] = (raw.variants ?? []).map(v => ({
    variantId: v.variantId,
    base: normBase(v.type),
    lengthCm: Number(v.length) || 0,
    price: Number(v.price) || 0,
    inventory: Number(v.inventory) || 0,
  }));
  const lr = raw.lengthRules;
  const bracelet = lr?.bracelet && Number(lr.bracelet.min) > 0
    ? { min: Number(lr.bracelet.min), max: Number(lr.bracelet.max) } : null;
  const necklace = lr?.necklace && Number(lr.necklace.min) > 0
    ? { min: Number(lr.necklace.min), max: Number(lr.necklace.max), base: Number(lr.necklace.base) } : null;
  const lengthRules = (bracelet || necklace)
    ? { bracelet, necklace, surchargePerCm: Number(lr?.surchargePerCm) || 0 } : undefined;
  return {
    id: raw.id, title: raw.title, chainType: raw.chainType ?? '', variants,
    image: raw.ringImage || null,
    closeUpImage: raw.closeUpImage || null,
    closeUpFocal: raw.closeUpFocal || '50% 50%',
    colors: toArray(raw.colors),
    lengthRules,
  };
}

function parseClosure(raw: RawClosure): ClosureItem {
  return {
    id: raw.id,
    variantId: raw.variantId,
    title: raw.title,
    price: Number(raw.price) || 0,
    available: Number(raw.inventory) || 0,
    image: raw.image || null,
    colors: toArray(raw.colors),
    forBag: !!raw.forBag,
    forKeychain: !!raw.forKeychain,
  };
}

function parseCharm(raw: RawCharm): CharmCatalogItem {
  return {
    productId: raw.id,
    variantId: raw.variantId ?? '',
    title: raw.title,
    price: Number(raw.price) || 0,
    image: raw.charmImage || null,
    available: Number(raw.inventory) || 0,
    attributes: {
      size: sizeCode(raw.size),
      sizeLabel: raw.size ?? null,
      material: raw.material ?? null,
      theme: toArray(raw.theme),
      colors: toArray(raw.color),
      waterproof: !!raw.waterproof,
      original: !!raw.original,
    },
  };
}

/** Base cards from the blob; falls back to BASE_CONFIG defaults (no image) when the
 *  section hasn't authored them, so step 1 always has its 4 cards. */
function parseBases(raw: RawBase[] | undefined): BaseCard[] {
  const order = Object.keys(BASE_CONFIG) as BaseKind[];
  const byKind = new Map((raw ?? []).map(b => [b.kind, b]));
  return order.map(kind => {
    const def = BASE_CONFIG[kind];
    const b = byKind.get(kind);
    return {
      kind,
      label: b?.label || def.label,
      image: b?.image || null,
      maxCharms: Number(b?.maxCharms) || def.maxCharms,
      fromPrice: Number(b?.fromPrice) || def.fromPrice,
      comingSoon: b?.comingSoon === true,
    };
  });
}

/** Parse the flat blob into the typed catalog + generated facets. */
export function loadCatalog(data: ConfiguratorData): Catalog {
  const charms = (data.charms ?? []).map(parseCharm);
  // Optional admin-chosen size ordering (section setting `charm_sort`). Applied
  // once here, after all charm pages are merged (index.ts), so it spans the full
  // set — not just page 1. Array.sort is stable, so charms of the same size keep
  // their collection order as the tiebreaker.
  const sortMode = data.charmSort;
  if (sortMode === 'size_asc' || sortMode === 'size_desc') {
    const dir = sortMode === 'size_desc' ? -1 : 1;
    charms.sort((a, b) => dir * (sizeRank(a.attributes.size) - sizeRank(b.attributes.size)));
  }
  // Real chains first, then append a synthetic length-less chain per palette combo so
  // canvas/pricing/cart/serialize keep resolving palettes by `chain.id` unchanged.
  const chains = (data.chains ?? []).map(parseChain);
  const palettes = parsePalettes(data.palettes ?? [], chains);
  return {
    chains,
    charms,
    bases: parseBases(data.bases),
    step0: {
      title: data.step0?.title || 'You design your piece. We bring it to life.',
      buttonLabel: data.step0?.buttonLabel || 'Start building',
      image: data.step0?.image || null,
      imageMobile: data.step0?.imageMobile || null,
      video: data.step0?.video || null,
      videoMobile: data.step0?.videoMobile || null,
      focalDesktop: data.step0?.focalDesktop || '50% 50%',
      focalMobile: data.step0?.focalMobile || '50% 50%',
      icon: data.step0?.icon || 'paopao-one',
      iconColor: data.step0?.iconColor || '#C8D7EA',
    },
    step1: {
      heading: data.step1?.heading || 'What do you want to make?',
      subtext: data.step1?.subtext || 'Choose your base. The fun part comes next.',
    },
    lengthHelp: {
      bracelet: data.lengthHelp?.bracelet || '',
      necklace: data.lengthHelp?.necklace || '',
      bag_charm: data.lengthHelp?.bag_charm || '',
      keychain: data.lengthHelp?.keychain || '',
    },
    beadsHelp: data.beadsHelp || '',
    backdropImage: data.backdropImage || null,
    backdropFocal: data.backdropFocal || '50% 50%',
    reviewBackdrop: data.reviewBackdrop || null,
    reviewBackdropFocal: data.reviewBackdropFocal || '50% 50%',
    reviewBackdropMobile: data.reviewBackdropMobile || null,
    reviewBackdropMobileFocal: data.reviewBackdropMobileFocal || '50% 50%',
    bundleParentVariantId: data.bundleParentVariantId || null,
    originalBadgeIcon: data.originalBadgeIcon || null,
    closures: (data.closures ?? []).map(parseClosure),
    palettes,
    surchargeVariantId: data.surchargeVariantId || null,
    surchargeUnitPrice: Number(data.surchargeUnitPrice) || 0,
    sizes: {
      charm: {
        bracelet: data.sizes?.charm?.bracelet ?? DEFAULT_SIZES.charm.bracelet,
        necklace: data.sizes?.charm?.necklace ?? DEFAULT_SIZES.charm.necklace,
        bag_charm: data.sizes?.charm?.bag_charm ?? DEFAULT_SIZES.charm.bag_charm,
        keychain: data.sizes?.charm?.keychain ?? DEFAULT_SIZES.charm.keychain,
      },
      closure: {
        bag_charm: data.sizes?.closure?.bag_charm ?? DEFAULT_SIZES.closure.bag_charm,
        keychain: data.sizes?.closure?.keychain ?? DEFAULT_SIZES.closure.keychain,
      },
    },
    facets: buildFacets(charms),
  };
}

// ─── DOM entry point (reads the Liquid-rendered JSON) ─────────────────────────

/** Read & parse the JSON blob the Liquid section renders. Returns null if the
 *  tag is missing or malformed. */
export function readConfiguratorData(doc: Document = document, elementId = 'configurator-data'): ConfiguratorData | null {
  const el = doc.getElementById(elementId);
  if (!el || !el.textContent) return null;
  try {
    return JSON.parse(el.textContent) as ConfiguratorData;
  } catch (err) {
    console.error('[configurator] failed to parse #configurator-data JSON', err);
    return null;
  }
}

/** Convenience: read the DOM blob and build the catalog (empty if absent). */
export function loadCatalogFromDOM(doc: Document = document): Catalog {
  const data = readConfiguratorData(doc);
  return data
    ? loadCatalog(data)
    : {
        chains: [], charms: [], bases: parseBases(undefined),
        step0: { title: 'You design your piece. We bring it to life.', buttonLabel: 'Start building', image: null, imageMobile: null, video: null, videoMobile: null, focalDesktop: '50% 50%', focalMobile: '50% 50%', icon: 'paopao-one', iconColor: '#C8D7EA' },
        step1: { heading: 'What do you want to make?', subtext: 'Choose your base. The fun part comes next.' },
        lengthHelp: { bracelet: '', necklace: '', bag_charm: '', keychain: '' }, beadsHelp: '',
        backdropImage: null, backdropFocal: '50% 50%', reviewBackdrop: null, reviewBackdropFocal: '50% 50%', reviewBackdropMobile: null, reviewBackdropMobileFocal: '50% 50%', bundleParentVariantId: null, originalBadgeIcon: null, closures: [], palettes: [], surchargeVariantId: null, surchargeUnitPrice: 0, sizes: DEFAULT_SIZES, facets: [],
      };
}

/** Bridge a catalog charm into the state layer's CharmProduct shape. */
export function toCharmProduct(item: CharmCatalogItem): CharmProduct {
  return { productId: item.productId, title: item.title, price: item.price, size: item.attributes.size };
}

// ─── Saved-build restore (favorites) ─────────────────────────────────────────

export interface Hydrated {
  build: BuildState;
  /** How many saved charms could NOT be restored (discontinued or now out of stock). */
  dropped: number;
}

/**
 * Rebuild a live BuildState from a serialized `_build` JSON against the CURRENT
 * catalog: chain by id, charms by productId (so price/size/title are live, not stale).
 * Charms that no longer exist or whose live stock can't cover the saved quantity are
 * dropped and counted, so the editor never loads an un-orderable piece. A fresh
 * groupId is assigned (via createBuild) so a re-added piece bundles on its own.
 */
export function hydrateBuild(catalog: Catalog, json: BuildJSON): Hydrated {
  const build = createBuild(json.base);
  const chain = catalog.chains.find(c => c.id === json.chainId) ?? null;
  setChain(build, chain);
  // Re-resolve the closure against the CURRENT catalog (live price/availability),
  // still valid for this chain. Falls back to null if it's gone or no longer matches.
  const savedClosureId = json.closure?.id ?? null;
  const liveClosure = savedClosureId
    ? closuresForChain(catalog, chain).find(c => c.id === savedClosureId && c.available > 0)
    : undefined;
  setClosure(build, liveClosure ? toClosureSelection(liveClosure) : null);
  build.length = json.length;

  let dropped = 0;
  const added = new Map<string, number>(); // per-product count, capped at live stock
  for (const cj of json.charms) {
    const item = catalog.charms.find(ci => ci.productId === cj.productId);
    const used = item ? (added.get(cj.productId) ?? 0) : 0;
    if (!item || used >= item.available) { dropped++; continue; } // missing / sold out / stock exhausted
    const uid = addCharm(build, toCharmProduct(item), cj.t);
    if (!uid) { dropped++; continue; }                            // blocked by base max-charms
    added.set(cj.productId, used + 1);
  }
  return { build, dropped };
}

/**
 * Can a saved build be added to the cart as-is? False when its chain no longer
 * resolves or any charm is discontinued / understocked for the saved quantity.
 * Drives the recent-creations card (ADD TO BAG vs the "some charms sold out" tag).
 */
export function buildAvailability(catalog: Catalog, json: BuildJSON): { addable: boolean } {
  if (!catalog.chains.find(c => c.id === json.chainId)) return { addable: false };
  const counts = new Map<string, number>();
  for (const c of json.charms) counts.set(c.productId, (counts.get(c.productId) ?? 0) + 1);
  for (const [pid, qty] of counts) {
    const item = catalog.charms.find(ci => ci.productId === pid);
    if (!item || item.available < qty) return { addable: false };
  }
  return { addable: true };
}
